import { ChangeDetectionStrategy, Component, computed, inject, model, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdChartManagerComponent } from './hd-chart-manager.component';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdChartCalculationResult } from './hd-chart-library.models';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HdRaveNewYearRequest } from '../../transits/rave-new-year/hd-rave-new-year.service';

@Component({
  selector: 'hd-chart-manager-dialog',
  standalone: true,
  imports: [SharedModule, HdChartManagerComponent],
  templateUrl: './hd-chart-manager-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartManagerDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  readonly loadChartRequested = output<{ id: string; preset: HdBodygraphChartPreset; type: PlatformChartType }>();
  /** "Enkel berekenen" in de manager: de pagina opent de chart als onopgeslagen tab; dialog sluit. */
  readonly calculatedChartRequested = output<HdChartCalculationResult>();
  /** Doorgelust vanaf de manager: verwijderde chart-ids (host sluit open tabs). */
  readonly chartsDeleted = output<string[]>();
  /** Doorgelust vanaf de manager: bewerkte chart-id (host ververst open tabs van die chart). */
  readonly chartUpdated = output<string>();
  /** Snelle actie "Spelmodus" in de manager → de pagina opent de spelkeuze-dialog. */
  readonly gameRequested = output<void>();
  /** Snelle actie "Just Now Transit" → de pagina laadt het moment (personality-only); dialog sluit. */
  readonly justNowRequested = output<void>();
  /** Snelle actie "Just Now Volledig" → de pagina laadt de volledige chart van nu; dialog sluit. */
  readonly justNowFullRequested = output<void>();
  /** Snelle actie "Rave New Year" → de pagina opent de chart van dat jaar (gekozen variant); dialog sluit. */
  readonly raveNewYearRequested = output<HdRaveNewYearRequest>();
  /** Snelle actie "Toon lege chart" → de pagina opent een verse lege tab; dialog sluit. */
  readonly emptyChartRequested = output<void>();
  /** Rij-actie "Tijdlijn" doorgelust naar de host (stap 5). */
  readonly timelineRequested = output<string>();

  protected readonly title = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate<string>(
      'humanDesign.chartManager.title',
      undefined,
      this.contentLang.effectiveLang(),
    );
  });

  onLoad(payload: { id: string; preset: HdBodygraphChartPreset; type: PlatformChartType }): void {
    this.loadChartRequested.emit(payload);
    this.open.set(false);
  }

  onCalculated(result: HdChartCalculationResult): void {
    this.calculatedChartRequested.emit(result);
    this.open.set(false);
  }

  onJustNow(): void {
    this.justNowRequested.emit();
    this.open.set(false);
  }

  onJustNowFull(): void {
    this.justNowFullRequested.emit();
    this.open.set(false);
  }

  onRaveNewYear(request: HdRaveNewYearRequest): void {
    this.raveNewYearRequested.emit(request);
    this.open.set(false);
  }

  onEmptyChart(): void {
    this.emptyChartRequested.emit();
    this.open.set(false);
  }
}
